import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from "react";
import { cn } from "../../lib/cn";
import { Icon } from "./Icon";

// ---------------------------------------------------------------------------
// Select — native <select> dressed up to match Input
// ---------------------------------------------------------------------------

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, invalid, children, ...props }, ref) => (
    <div className="relative">
      <select
        ref={ref}
        className={cn(
          "block h-9 w-full appearance-none rounded-md border border-ink-200 bg-surface pr-9 pl-3 text-ink-900 text-sm",
          "transition-colors enabled:hover:border-ink-300",
          "focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20 dark:focus:ring-accent-500/30",
          "disabled:cursor-not-allowed disabled:bg-ink-50 disabled:text-ink-500",
          "aria-invalid:border-rose-500 aria-invalid:ring-2 aria-invalid:ring-rose-500/20",
          "dark:aria-invalid:border-rose-400 dark:aria-invalid:ring-rose-400/30",
          className,
        )}
        aria-invalid={invalid}
        {...props}
      >
        {children}
      </select>
      <div className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-ink-500">
        <Icon name="chevron-down" size="xs" />
      </div>
    </div>
  ),
);
Select.displayName = "Select";

// ---------------------------------------------------------------------------
// Checkbox — accessible, custom-styled
// ---------------------------------------------------------------------------

export interface CheckboxProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: ReactNode;
  hint?: string;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, label, hint, id, ...props }, ref) => {
    const inputId = id ?? `cb-${Math.random().toString(36).slice(2, 8)}`;
    return (
      <label
        htmlFor={inputId}
        className={cn(
          "flex cursor-pointer items-start gap-2.5",
          props.disabled && "cursor-not-allowed opacity-60",
          className,
        )}
      >
        <span className="relative mt-0.5 flex">
          <input
            ref={ref}
            id={inputId}
            type="checkbox"
            className={cn(
              "peer h-4 w-4 shrink-0 cursor-pointer appearance-none rounded border border-ink-300 bg-surface",
              "transition-colors enabled:hover:border-ink-500 enabled:hover:bg-ink-50 dark:enabled:hover:bg-ink-200",
              "checked:border-primary checked:bg-primary",
              "checked:enabled:hover:border-primary/90 checked:enabled:hover:bg-primary/90",
              "focus-visible:outline-2 focus-visible:outline-accent-500 focus-visible:outline-offset-2",
              "disabled:cursor-not-allowed disabled:opacity-60",
            )}
            {...props}
          />
          <Icon
            name="check"
            size="xs"
            className="pointer-events-none absolute inset-0 m-auto text-primary-fg opacity-0 peer-checked:opacity-100"
          />
        </span>
        {label && (
          <div className="flex flex-col">
            <span className="text-ink-800 text-sm">{label}</span>
            {hint && <span className="text-ink-500 text-xs">{hint}</span>}
          </div>
        )}
      </label>
    );
  },
);
Checkbox.displayName = "Checkbox";

// ---------------------------------------------------------------------------
// Switch — toggle for boolean state
// ---------------------------------------------------------------------------

export interface SwitchProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  label?: ReactNode;
  hint?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
  /** Accessible name when no visible `label` is rendered. */
  "aria-label"?: string;
}

export function Switch({
  checked,
  onCheckedChange,
  label,
  hint,
  disabled,
  id,
  className,
  "aria-label": ariaLabel,
}: SwitchProps) {
  const inputId = id ?? `sw-${Math.random().toString(36).slice(2, 8)}`;
  return (
    <label
      htmlFor={inputId}
      className={cn(
        "flex cursor-pointer items-center gap-3",
        disabled && "cursor-not-allowed opacity-60",
        className,
      )}
    >
      {/* Raw <button> on purpose: the toggle is intentionally 20×36 for the
        switch aesthetic — WCAG's 24×24 floor is met by the surrounding
        <label>, which is the effective tap target via htmlFor. */}
      <button
        id={inputId}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => onCheckedChange(!checked)}
        className={cn(
          "relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full transition-colors",
          "focus-visible:outline-2 focus-visible:outline-accent-500 focus-visible:outline-offset-2",
          "disabled:cursor-not-allowed",
          checked
            ? "bg-primary enabled:hover:bg-primary/90"
            : "bg-ink-300 enabled:hover:bg-ink-400 dark:bg-ink-400 dark:enabled:hover:bg-ink-500",
        )}
      >
        <span
          className={cn(
            "mt-0.5 inline-block h-4 w-4 rounded-full bg-surface shadow-subtle motion-safe:transition-transform",
            checked ? "translate-x-4" : "translate-x-0.5",
          )}
        />
      </button>
      {label && (
        <div className="flex flex-col">
          <span className="text-ink-800 text-sm">{label}</span>
          {hint && <span className="text-ink-500 text-xs">{hint}</span>}
        </div>
      )}
    </label>
  );
}
